import SelectBox from 'devextreme-react/select-box';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import { KpiTile } from '../../components/KpiTile';
import type { UserActivityAnalyticsGeoSection, UserActivityReportMetric } from '../types';
import type { UserActivityGeoSubview } from '../userActivityPrefs';
import { DimensionReportPanel } from './DimensionReportPanel';
import { drilldownForDimension } from './sessionDrilldown';

const SUBVIEW_OPTIONS: { value: UserActivityGeoSubview; text: string }[] = [
  { value: 'country', text: 'Country' },
  { value: 'continent', text: 'Continent' },
  { value: 'asn', text: 'ASN' },
  { value: 'asName', text: 'AS organization' },
  { value: 'asDomain', text: 'AS domain' },
  { value: 'timezone', text: 'User timezone' },
  { value: 'locale', text: 'Locale' },
  { value: 'coverage', text: 'Geo coverage' }
];

export function GeoReport({
  geo,
  chartColors,
  subview,
  onSubviewChange,
  metric,
  onMetricChange,
  onDrilldown
}: {
  geo: UserActivityAnalyticsGeoSection;
  chartColors: ChartThemeColors;
  subview: UserActivityGeoSubview;
  onSubviewChange: (next: UserActivityGeoSubview) => void;
  metric: UserActivityReportMetric;
  onMetricChange: (next: UserActivityReportMetric) => void;
  onDrilldown: (label: string, filter: unknown) => void;
}) {
  const rows =
    subview === 'continent'
      ? geo.byContinent
      : subview === 'asn'
        ? geo.byAsn
        : subview === 'asName'
          ? geo.byAsName
          : subview === 'asDomain'
            ? geo.byAsDomain
            : subview === 'timezone'
              ? geo.byTimezone
              : subview === 'locale'
                ? geo.byLocale
                : subview === 'coverage'
                  ? geo.byGeoResolved
                  : geo.byCountry;

  const dim =
    subview === 'coverage'
      ? 'geoResolved'
      : subview === 'asName'
        ? 'asName'
        : subview === 'asDomain'
          ? 'asDomain'
          : subview;

  return (
    <div className="user-activity-analytics__body user-activity-analytics__body--scroll">
      <div className="user-activity-analytics__chart-header">
        <div>
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Geo &amp; network
          </h3>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--ops-color-muted, #6b7280)', fontSize: 14 }}>
            Raw IP addresses and full user-agent strings stay in the Sessions detail view.
          </p>
        </div>
        <SelectBox
          dataSource={SUBVIEW_OPTIONS}
          value={subview}
          valueExpr="value"
          displayExpr="text"
          width={180}
          onValueChanged={(e) => onSubviewChange(e.value as UserActivityGeoSubview)}
        />
      </div>

      <section className="user-activity-analytics__kpi-row">
        <KpiTile label="Geo resolved %" value={`${geo.coverage.geoResolvedPercent}%`} />
        <KpiTile label="Resolved sessions" value={String(geo.coverage.geoResolvedSessions)} />
        <KpiTile label="Unresolved sessions" value={String(geo.coverage.geoUnresolvedSessions)} />
        <KpiTile label="With timezone" value={String(geo.coverage.withTimezone)} />
        <KpiTile label="With locale" value={String(geo.coverage.withLocale)} />
      </section>

      <DimensionReportPanel
        title={
          subview === 'coverage'
            ? 'Resolved vs unresolved'
            : (SUBVIEW_OPTIONS.find((o) => o.value === subview)?.text ?? 'Geo')
        }
        subtitle={
          subview === 'coverage'
            ? 'Whether IP geo lookup succeeded for the session.'
            : undefined
        }
        rows={rows}
        colors={chartColors}
        metric={metric}
        onMetricChange={onMetricChange}
        fileNamePrefix={`geo-${subview}`}
        onRowDrilldown={(row) => {
          const drill = drilldownForDimension(
            dim as
              | 'country'
              | 'continent'
              | 'asn'
              | 'asName'
              | 'asDomain'
              | 'timezone'
              | 'locale'
              | 'geoResolved',
            row.key,
            row.label
          );
          onDrilldown(drill.label, drill.filter);
        }}
      />
    </div>
  );
}
